import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, X, ArrowRight, FileText } from 'lucide-react';

export const StickyCallBar = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-primary shadow-2xl border-t border-primary/80 transform transition-transform duration-300 md:hidden" data-testid="sticky-call-bar">
      <div className="flex items-center">
        <a href="tel:07704885084" className="flex-1 flex items-center justify-center gap-2 py-4 text-white font-bold text-sm">
          <Phone className="h-4 w-4" />
          Call 07704 885084
        </a>
        <Link to="/contact" className="flex-1 flex items-center justify-center gap-2 py-4 bg-white text-primary font-bold text-sm border-l border-primary/20">
          <FileText className="h-4 w-4" />
          Free Quote
        </Link>
      </div>
    </div>
  );
};

export const FloatingQuoteBar = () => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!dismissed) setVisible(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [dismissed]);

  if (!visible || dismissed) return null;

  return (
    <div className="fixed bottom-6 right-6 z-40 hidden md:block" data-testid="floating-quote-bar">
      <div className="bg-white rounded-lg shadow-2xl border border-border p-5 max-w-xs animate-in slide-in-from-bottom-4">
        <button onClick={() => setDismissed(true)} className="absolute top-2 right-2 text-gray-400 hover:text-gray-600">
          <X className="h-4 w-4" />
        </button>
        <p className="text-sm font-bold text-secondary mb-1">Need an Asbestos Survey?</p>
        <p className="text-xs text-muted-foreground mb-3">Same-day booking. Reports in 24-48hrs. BOHS P402 qualified.</p>
        <div className="flex flex-col gap-2">
          <a href="tel:07704885084" className="flex items-center justify-center gap-2 bg-primary text-white rounded px-4 py-2 text-sm font-medium hover:bg-primary/90 transition-colors">
            <Phone className="h-3.5 w-3.5" /> 07704 885084
          </a>
          <Link to="/contact" className="flex items-center justify-center gap-2 border border-primary text-primary rounded px-4 py-2 text-sm font-medium hover:bg-primary/5 transition-colors" onClick={() => setDismissed(true)}>
            Get a Free Quote <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export const ExitIntentPopup = () => {
  const [shown, setShown] = useState(false);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    if (closed) return;
    const handler = (e) => {
      if (e.clientY < 10 && !shown && !closed) setShown(true);
    };
    document.addEventListener('mouseleave', handler);
    return () => document.removeEventListener('mouseleave', handler);
  }, [shown, closed]);

  if (!shown || closed) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="exit-popup">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-8 relative animate-in zoom-in-95">
        <button onClick={() => { setShown(false); setClosed(true); }} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
          <X className="h-5 w-5" />
        </button>
        <div className="text-center">
          <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="h-7 w-7 text-primary" />
          </div>
          <h3 className="text-xl font-bold text-secondary mb-2">Before You Go...</h3>
          <p className="text-sm text-muted-foreground mb-6">Download our free Asbestos Compliance Guide — essential reading for every dutyholder and property manager.</p>
          <div className="flex flex-col gap-3">
            <Link
              to="/guides"
              onClick={() => { setShown(false); setClosed(true); }}
              className="flex items-center justify-center gap-2 bg-primary text-white rounded-lg px-6 py-3 text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              Download Free Guide <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="tel:07704885084" className="flex items-center justify-center gap-2 text-primary text-sm font-medium hover:underline">
              <Phone className="h-4 w-4" /> Or call us: 07704 885084
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
